import React, { useState, useEffect, useRef } from 'react';
import CreatableSelect from 'react-select/creatable';
import Layout from '../components/Layout';
import { FaEdit, FaSave } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import Loader from "../components/Loader"
import { PiDotsThree } from 'react-icons/pi';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import FilterSection from '../components/FilterSection';
import Pagination from '../components/Pagination';
import SearchableDropdown from '../components/SearchableDropdown';
import { toast } from 'react-toastify';
import ComboBox from '../components/ComboBox';
import Spinner from '../components/Spinner';
import { generateDropoffTrackingNumber } from '../utils/generateTrackingNumber';
import DataTable from '../components/DataTable';
import DateFormatterService from '../utils/DateFormatterService';
import { BiPrinter } from 'react-icons/bi';
import PackageDetailsModal from '../components/PackageDetailsModal';
import { FaCloudUploadAlt } from "react-icons/fa";

const Packages = () => {
  const [filteredPackages, setFilteredPackages] = useState([]);
  const initialPackageData = {
    account_id: "",
    consignee: "",
    delivery_service_id: "",
    description: "",
    notes: "",
    package_value: "",
    shipment_id: "",
    shipper_id: "",
    staff_id: "",
    tracking_number: "",
    weight: "",
    package_type_id: "",
    shipment_type_id: "",
    statusHistories: []
  };

  const [packageData, setPackageData] = useState(initialPackageData);

  const user = JSON.parse(localStorage.getItem('user'));

  const [metaData, setMetaData] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 0,
  });

  const [packages, setPackages] = useState([]);
  const [packageHistory, setPackagesHistory] = useState([]);
  const [deliveryService, setDeliveryService] = useState([]);


  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);

  const [shipments, setShipment] = useState([]);
  const [packageType, setPackageType] = useState([]);
  const [shipmentMethod, setShipmentMethod] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [status, setStatus] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [cleanedBarcode, setCleanedBarcode] = useState('');
  const [viewMode, setViewMode] = useState(false);
  const [noteMode, setNoteMode] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPackageId, setCurrentPackageId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const timeoutRef = useRef(null);
  const [inputTimeout, setInputTimeout] = useState(null);
  const firstInputRef = useRef(null);
  const dialogRef = useRef(null);
  const [activeTab, setActiveTab] = useState('packages');
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // List of special keys to ignore
  const ignoredKeywords = [
    'Clear', 'Shift', 'Enter', 'Scroll', 'Tab', 'Backspace',
    'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock',
    'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'Escape', 'Insert', 'Delete', 'Home', 'End', 'PageUp', 'PageDown',
    'NumLock', 'ScrollLock', 'Pause', 'PrintScreen', 'ContextMenu'
  ];

  const fetchBarcodeData = async (rawBarcode) => {
    setLoading(true)
    try {
      const response = await fetch(`${apiUrl}/api/packages/barcode?barcode=${encodeURIComponent(rawBarcode)}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setCleanedBarcode(data.cleanedBarcode);
        // Handle cleaned barcode (submit form, update state, etc.)
        processBarcodeInput();
        console.log('Cleaned barcode:', data.cleanedBarcode);
      } else {
        throw new Error(data.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Barcode processing failed:', error);
      alert(`Barcode error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
      setCleanedBarcode('')
    }
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check if the focused element is an input, textarea, or content-editable element
      const activeElement = document.activeElement;
      if (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      ) {
        return; // Ignore keypresses when in an input field
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const key = event.key;

      // Handle Enter key submission
      if (key === 'Enter' && !modalOpen) {
        event.preventDefault();
        if (barcodeBuffer.length > 0) {
          fetchBarcodeData(barcodeBuffer);
          setBarcodeBuffer('');
        }
        return;
      }

      // Ignore special keys
      if (ignoredKeywords.includes(key)) return;

      // Add character to buffer
      setBarcodeBuffer((prev) => prev + key);

      // Set up a 1-second timeout; if no key is pressed during this time, reset the buffer
      timeoutRef.current = setTimeout(() => {
        setBarcodeBuffer('');
      }, 2000);
    };

    // Add global event listener
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [barcodeBuffer, modalOpen]);

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.showModal(); // Ensure dialog is opened
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus(); // Auto-focus the first input after a short delay
        }
      }, 100); // Delay to allow rendering
    }
  }, []);

  const processBarcodeInput = () => {
    fetchDeliveryService();
    fetchShipment();
    fetchShipmentMethod();
    fetchPackageType();
    fetchCouriers();
    fetchAccounts();
    fetchStatus();
    const isExist = packages.find(item => item.tracking_number === barcodeBuffer)
    if (isExist) {
      setPackageData(isExist);
      setCurrentPackageId(isExist.id)
      setEditMode(true);
      setModalOpen(true);
    }
    else if (barcodeBuffer.length >= 5) {

      const delivery_servic_result = deliveryService.find(item =>
        String(item.pattern).substring(0, 3) === barcodeBuffer.substring(0, 3)
      )?.id ?? "";
      setPackageData((prevData) => ({
        ...prevData,
        tracking_number: barcodeBuffer,
        delivery_service_id: delivery_servic_result
      }));
      setModalOpen(true); // Open the modal after processing the buffer
    }
    // Clear the buffer after processing
    setBarcodeBuffer(''); // Clear the buffer immediately after processing
  };



  // const handleKeyPress = (e) => {
  //   const key = e.key;

  //   // If the key is an ignored keyword, handle it accordingly
  //   if (ignoredKeywords.includes(key)) {
  //     if (key === 'Enter' && !modalOpen) {
  //       processBarcodeInput(); // Process the buffer if Enter is pressed
  //     }
  //     return; // Ignore other keywords
  //   }

  //   // Append the valid character to the barcode buffer
  //   setBarcodeBuffer((prevBuffer) => prevBuffer + key);

  //   // Clear previous timeout
  //   if (inputTimeout) {
  //     clearTimeout(inputTimeout);
  //   }

  //   // Set a timeout to process the input if no key is pressed for 1 second
  //   setInputTimeout(
  //     setTimeout(() => {
  //       // processBarcodeInput(); // Process the buffer after timeout
  //     }, 1000) // 1 second timeout
  //   );
  // };

  const handleOpenModal = () => {
    try {
      setEditMode(false);
      setPackageData(initialPackageData);
      setModalOpen(true)
      setLoading(true);
      fetchDeliveryService();
      fetchShipment();
      fetchCouriers();
      fetchAccounts();
      fetchPackageType();
      fetchShipmentMethod();
      fetchStatus();
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false);
    }
  }
  const handleViewModal = (data) => {

    setViewMode(true);
    setPackageData(data);

  }

  // Select field configuration
  const selectConfig = (options, field, placeholder, allowCreate = true) => ({
    options,
    placeholder,
    isClearable: true,
    // onChange: (selectedOption) => {

    //   setPackageData((prev) => ({
    //     ...prev,
    //     [field]: selectedOption ? selectedOption.value : "",
    //   }));
    // },
    onChange: (selectedOption) => handleSelectChange(selectedOption, field), // Pass field name

    isValidNewOption: (inputValue) => allowCreate && inputValue.trim().length > 0, // Allow creation only when enabled
    formatCreateLabel: (inputValue) => `Create "${inputValue}"`,
  });


  // Function to handle filter changes
  const handleFilterChange = (filters) => {
    let filtered = [...packages];

    // Filter by search
    if (filters.search) {
      filtered = filtered.filter(
        (pkg) =>
          pkg.tracking_number?.toLowerCase().includes(filters.search.toLowerCase()) ||
          pkg.description?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(
        (pkg) => new Date(pkg.created_at) >= new Date(filters.startDate) // Adjust `created_at` field if necessary
      );
    }
    if (filters.endDate) {
      filtered = filtered.filter(
        (pkg) => new Date(pkg.created_at) <= new Date(filters.endDate) // Adjust `created_at` field if necessary
      );
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter((pkg) =>
        pkg.statusHistories.some((history) => history.status?.id === filters.status)
      );
    }

    // Separate filtered data into processed and unprocessed
    const newProcessed = filtered.filter(
      (pkg) => pkg.tracking_number && pkg.courier?.id && pkg.deliveryService?.id && pkg.accounts?.id
    );

    // Update state
    setPackages(newProcessed); // Update processed packages
  };


  useEffect(() => {
    setFilteredPackages([...packages]);
  }, [packages]);


  // State for search query
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = React.useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  // Ref for AbortController to handle request cancellation
  const abortControllerRef = useRef(null);

  const fetchPackages = async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create new controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);
    try {
      const url = `${apiUrl}/api/packages?page=${currentPage}&limit=50&search=${searchQuery}${user.role === "Courier" ? `&courierId=${user.courier_id}` : ""}`;

      const response = await fetch(`${url}`, { signal });
      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }
      const data = await response.json();

      setPackages(data.results ?? []);
      setMetaData(data.meta);
      setPackagesHistory(data.statusHistories);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
        return;
      }
      setError(error.message);
    } finally {
      // Only unset loading if this was the last request (controller matches)
      // Actually, standard finally runs even on abort, but we checked error type.
      // If aborted, we don't want to turn off loading if another request immediately started?
      // With the ref logic, the next request sets loading=true. 
      // If we are here because of abort, we shouldn't touch loading state if we want to be perfect, 
      // but usually the next request's setLoading(true) happens immediately after abort() call.
      // However, to be safe, we can check if the signal is aborted.
      if (!signal.aborted) {
        setLoading(false);
        if (printMode) {
          handleDownload(packageData.tracking_number);
        }
        if (viewMode) {
          getPackageById(packageData.tracking_number);
        }
      }
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [currentPage, searchQuery]);




  const getPackageById = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/packages/${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPackageData(data)
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);

    }
  }


  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error('Please select a file to upload');
      return;
    }

    const toastId = toast.loading('Uploading packages...');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('staff_id', user.id);

      const response = await fetch(`${apiUrl}/api/packages/bulk`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload packages');
      }

      const result = await response.json();

      const successMessage = result.errorCount > 0
        ? `Bulk upload complete: ${result.successCount} saved, ${result.errorCount} failed.`
        : `Successfully imported all ${result.successCount} packages!`;

      toast.update(toastId, {
        render: successMessage,
        type: result.errorCount > 0 ? 'warning' : 'success',
        isLoading: false,
        autoClose: 5000,
      });

      if (result.errors && result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }

      // Close modal and refresh packages
      setShowBulkModal(false);
      setBulkFile(null);
      fetchPackages();

    } catch (error) {
      console.error('Error uploading bulk packages:', error);
      toast.update(toastId, {
        render: `Error uploading packages: ${error.message}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const fetchShipment = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/shipments/all`);
      if (!response.ok) {
        throw new Error('Failed to fetch shipments');
      }
      const data = await response.json();
      setShipment(data);

    } catch (error) {
      setError(error.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShipmentMethod = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/shipmentMethod`);
      if (!response.ok) {
        throw new Error('Failed to fetch shipments');
      }
      const data = await response.json();
      setShipmentMethod(data);

    } catch (error) {
      setError(error.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageType = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/packageType`);
      if (!response.ok) {
        throw new Error('Failed to fetch shipments');
      }
      const data = await response.json();
      setPackageType(data);

    } catch (error) {
      setError(error.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/accounts/all`);
      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }
      const data = await response.json();
      setAccounts(data);
    } catch (error) {
      setError(error.message);
      console.log(error);

    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/status`);
      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      setError(error.message);
      console.log(error);

    } finally {
      setLoading(false);
    }
  };


  const fetchDeliveryService = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/api/delivery-service`);
      if (!response.ok) throw new Error('Failed to fetch delivery services');

      const data = await response.json();

      console.log('📌 Raw delivery service data from API:', data);

      const servicesWithRegex = data.map(service => {
        try {
          if (!service.pattern) throw new Error(`🚨 Missing pattern for ${service.name}`);

          let regexPattern = typeof service.pattern === 'string'
            ? service.pattern.trim()
            : JSON.stringify(service.pattern).trim();

          let flags = 'i'; // Default case-insensitive

          const match = regexPattern.match(/^\/(.+)\/([a-z]*)$/i);
          if (match) {
            regexPattern = match[1];
            flags = match[2] || 'i';
          } else {
            regexPattern = regexPattern.replace(/^\/|\/[a-z]*$/gi, '');
          }

          if (!regexPattern) throw new Error(`⚠️ Empty regex pattern for ${service.name}`);

          const regex = new RegExp(regexPattern, flags);
          console.log(`✅ Converted regex for ${service.name}:`, regex);

          return {
            ...service,
            pattern: regex // 🛠 Keep as `RegExp` object
          };
        } catch (e) {
          console.error(`❌ Invalid regex for ${service.name}:`, service.pattern, e);
          return {
            ...service,
            pattern: /^$/i // Fallback (never matches)
          };
        }
      });

      // 🔥 Store regex as `RegExp` objects in state
      setDeliveryService(servicesWithRegex);

    } catch (error) {
      setError(error.message);
      console.error('❌ Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCouriers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/courier/all`);
      if (!response.ok) {
        throw new Error("Failed to fetch Couriers");
      }
      const data = await response.json();
      setCouriers(data);
    } catch (error) {
      setError(error.message);
      console.log(error);

    } finally {
      setLoading(false);
    }
  }

  // Validate tracking number against all services
  const checkTrackingNumber = (trackingNumber) => {
    const cleaned = trackingNumber.replace(/\s/g, ''); // Remove spaces

    // Ensure regex objects are not re-converted
    const validatedServices = deliveryService.map(service => {
      if (service.pattern instanceof RegExp) {
        return service; // 🛠 Keep as is if it's already a RegExp
      }

      let patternStr = service.pattern;
      if (typeof patternStr !== 'string') {
        console.error(`Invalid pattern for ${service.name}:`, patternStr);
        return { ...service, pattern: /^$/i }; // Default empty regex
      }

      try {
        const regexMatch = patternStr.match(/^\/(.+)\/([gimsuy]*)$/);
        if (regexMatch) {
          const [, patternBody, flags] = regexMatch;
          return { ...service, pattern: new RegExp(patternBody, flags) };
        }

        return { ...service, pattern: new RegExp(patternStr, "i") };

      } catch (err) {
        console.error(`Error parsing regex for ${service.name}:`, patternStr, err);
        return { ...service, pattern: /^$/i };
      }
    });

    // 🔥 Now correctly checks tracking number against regex
    const matchedService = validatedServices.find(service => {
      console.log(`🔍 Testing "${cleaned}" against ${service.name}:`, service.pattern);
      return service.pattern.test(cleaned);
    });

    console.log('✅ Matched Service:', matchedService?.name || 'None');

    if (matchedService) {
      setPackageData(prev => ({
        ...prev,
        delivery_service_id: matchedService.id
      }));
      return matchedService.name;
    }

    return null;
  };








  // useEffect(() => {
  //   if (couriers.length > 0 && !packageData.shipper) {
  //     setPackageData((prevData) => ({ ...prevData, shipper: couriers[0].name }));
  //   }

  //   if (shipments.length > 0 && !packageData.shipment) {
  //     setPackageData((prevData) => ({ ...prevData, shipment: shipments[0].shipmentNumber }));
  //   }

  //   if (deliveryService.length > 0 && !packageData.delivery_service) {
  //     setPackageData((prevData) => ({ ...prevData, delivery_service: deliveryService[0].name }));
  //   }
  // }, [couriers, shipments, deliveryService])

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name == 'tracking_number') {
      checkTrackingNumber(value);
    }

    if (value == 'DROPOFF') {
      console.log('DROPOFF')
      setPackageData(({ ...packageData, tracking_number: generateDropoffTrackingNumber(), }));

    }
    setPackageData((prevData) => ({ ...prevData, [name]: value }));
    console.log(packageData)
  };

  const saveNotes = async (newNotes) => {
    setLoading(true);
    setNoteMode(true); // This will trigger the useEffect hook to log the updated value
    try {
      const staffId = user.id;

      // Update the package data with the new notes and staff ID
      const updatedPackageData = {
        ...packageData,
        notes: newNotes,
        staff_id: staffId,
      };

      // Send the updated package data to the backend using a PUT request
      const response = await fetch(`${apiUrl}/api/packages`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedPackageData),
      });

      if (!response.ok) {
        throw new Error("Failed to update package data.");
      }

      const updatedPackage = await response.json();

      setPackageData(updatedPackage.package);
      setLoading(false);
    } catch (error) {
      console.error("Error updating package:", error.message);
      setError(error.message);
    } finally {
      fetchPackages();
    }
  }

  const handleSubmit = async (event) => {
    setLoading(true);

    event.preventDefault();
    setTimeout(() => {

    }, 100);
    try {
      let response;
      let message = '';

      // Include the staff_id in the packageData
      const staffId = user.id; // Replace this with the actual staff ID
      const updatedPackageData = {
        ...packageData,
        staff_id: staffId,
      };
      console.log(updatedPackageData)

      if (editMode) {

        try {
          // Ensure `staff_id` and `id` are included in the request payload
          const staffId = user.id; // Replace with dynamic staff ID from session or state
          const updatedPackageData = {
            ...packageData,
            staff_id: staffId,
            packageId: currentPackageId
          };

          // Update an existing package
          const response = await fetch(`${apiUrl}/api/packages`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedPackageData),
          });

          const data = await response.json();

          if (response.ok) {
            toast.success('Package updated successfully!');
          } else {
            throw new Error(`${data.error}`);
          }
        } catch (error) {
          console.error('Error updating package:', error);
          toast.error(`An error occurred: ${error.message}`);
        }

      } else {
        // Create a new package
        response = await fetch(`${apiUrl}/api/packages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedPackageData),
        });

        const data = await response.json();

        if (response.ok) {
          message = 'Package created successfully!';
        } else {
          throw new Error(`${data.error}`);
        }
      }

      // Notify the user of success
      if (message) {
        toast.success(message);
      }
    } catch (error) {
      // Handle errors gracefully
      console.error('Error submitting package:', error);
      toast.error(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
      const submittedTracking = packageData.tracking_number;
      setPackageData(initialPackageData);
      setBarcodeBuffer('');
      setModalOpen(false);
      fetchPackages();

      // Ensure actions that depend on the tracking_number work after state reset
      if (printMode) {
        handleDownload(submittedTracking);
        setPrintMode(false);
      }
      if (viewMode) {
        getPackageById(submittedTracking);
      }
    }
  };


  const handleEdit = (id) => {
    const packageToEdit = packages.find((pkg) => pkg.id === id);
    if (packageToEdit) {
      setPackageData(packageToEdit);
      setCurrentPackageId(id);
      setEditMode(true);
      setModalOpen(true);
      console.log(packageToEdit);
    }
  };

  const handleDownload = async (tracking_number) => {
    if (!tracking_number) {
      toast.error("Invalid tracking number for printing.");
      return;
    }

    const toastId = toast.loading("Preparing PDF label...");
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/api/generate-label/${encodeURIComponent(tracking_number)}`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate label on server.');
      }

      const pdfBlob = await response.blob();
      if (pdfBlob.size === 0) throw new Error("Generated PDF is empty.");

      const pdfURL = URL.createObjectURL(pdfBlob);

      // Attempt to open in new tab
      const printWindow = window.open(pdfURL, '_blank');

      if (!printWindow) {
        // Browser blocked the popup, provide a direct download link
        toast.update(toastId, {
          render: "Popup blocked! Click here to download.",
          type: "info",
          isLoading: false,
          autoClose: 10000,
          onClick: () => {
            const link = document.createElement('a');
            link.href = pdfURL;
            link.download = `Label-${tracking_number}.pdf`;
            link.click();
          }
        });
      } else {
        toast.update(toastId, {
          render: "Label generated and opened for printing.",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error in handleDownload:', error);
      toast.update(toastId, {
        render: `Printing failed: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      if (printMode) setPrintMode(false);
      setLoading(false);
    }
  };






  const handleDeleteModal = (id) => {
    const packageToDelete = packages.find((pkg) => pkg.id === id);
    if (packageToDelete) {
      setPackageData(packageToDelete);
      setCurrentPackageId(id);
      setModalDeleteOpen(true);
    }
  };

  const handleDelete = async (id) => {
    console.log(deleteId)
    try {
      await fetch(`${apiUrl}/api/packages/${id}`, {
        method: 'DELETE',
      });
      fetchPackages();
      setModalDeleteOpen(false);
    } catch (error) {
      console.error('Error deleting package:', error);
      setError('Error deleting package. Please try again.');

      setModalDeleteOpen(false);

    } finally {
      setDeleteId(null)
    }
  };


  const handleModalClose = () => {
    setModalOpen(false);
    setEditMode(false);
    setViewMode(false);
    setPrintMode(false);
    setCurrentPackageId(null);
    setPackageData(initialPackageData);
  };

  const columns = [
    {
      header: 'Product Name',
      accessor: 'name',
      sortable: true,
      width: '30%',
    },
    {
      header: 'Price',
      accessor: 'price',
      sortable: true,
      align: 'right',
      cell: (value) => `$${value.toFixed(2)}`,
    },
    {
      header: 'Category',
      accessor: 'category',
      sortable: true,
    },
    {
      header: 'Stock',
      accessor: 'stock',
      align: 'center',
      cell: (value) => (
        <span
          className={`px-2 py-1 rounded-full ${value > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
        >
          {value > 0 ? 'In Stock' : 'Out of Stock'}
        </span>
      ),
    },
    {
      header: 'Rating',
      accessor: 'rating',
      sortable: true,
      cell: (value) => (
        <span className="flex items-center gap-1">
          <span className="text-yellow-500">★</span>
          {value}
        </span>
      ),
    },
    {
      header: 'Release Date',
      accessor: 'releaseDate',
      sortable: true,
      cell: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  const packageColumns = [

    {
      name: 'Package',
      selector: (row) => row.airWayBill || 'N/A',
      sortable: true,
      cell: (row) => (
        <a
          href="#" // Prevent default link behavior
          className="text-blue-600 hover:underline"
          onClick={(e) => {
            e.preventDefault(); // Prevent unwanted navigation
            e.stopPropagation(); // Stop row click event
            handleViewModal(row); // Pass row data to modal function
          }}
        >
          {row.airWayBill || 'N/A'}
        </a>
      ),
    }
    ,
    {
      name: 'Description',
      selector: (row) => row.description || 'N/A',
      sortable: true,
    },
    {
      name: 'Shipper',
      selector: (row) => row.courier?.name || 'N/A',
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) =>
        row.statusHistories?.length > 0
          ? row.statusHistories[row.statusHistories.length - 1].status?.name || 'N/A'
          : 'N/A',
      sortable: true,
    },
    {
      name: 'Tracking Number',
      selector: (row) => row.tracking_number || 'N/A',
      sortable: true,
    },

    {
      name: 'Lbs',
      selector: (row) => row.weight || 0,
      sortable: true,
    },

    {
      name: 'Consignee',
      selector: (row) => row.consignee || 'N/A',
      sortable: true,
    },
    {
      name: 'Date',
      selector: (row) => DateFormatterService.formatToShortDate(row.date) || '0.0',
      sortable: true,

    },

    // {
    //   name: 'Actions',
    //   cell: (row) => (
    //     <div className="dropdown">
    //       <div tabIndex={0} role="button" className="btn m-1 bg-base-300">
    //         <PiDotsThree />
    //       </div>
    //       <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow">
    //         <li onClick={() => handleEdit(row.id)}><a>Edit</a></li>
    //         <li onClick={() => handleDownload(row.tracking_number)}><a>Print</a></li>
    //         <li onClick={() => setDeleteId(row.id)} className="text-red-500"><a>Delete</a></li>
    //       </ul>
    //     </div>
    //   ),
    // },
  ];

  const options = accounts.map((account) => ({
    name: 'account_id',
    value: account.id,
    label: `${account.first_name} ${account.last_name} | ${account.mailboxNumber} `,
  }));

  const shipmentsOptions = shipments.map((shipment) => ({
    value: shipment.id,
    label: shipment.shipment_number,
  }));

  const packageTypeOptions = packageType.map((package_type) => ({
    value: package_type.id,
    label: package_type.name,
  }));

  const shipmentMethodOptions = shipmentMethod.map((shipment) => ({
    value: shipment.id,
    label: shipment.name,
  }));

  const deliveryServiceOptions = [
    { value: "DROPOFF", label: "DROP OFF", pattern: "" }, // Static option
    ...deliveryService.map((ds) => ({
      value: ds.id,
      label: ds.name,
      pattern: ds.pattern,
    })),
  ];


  const couriersOptions = couriers.map((courier) => ({
    value: courier.id,
    label: courier.username,
  }));

  const handleSelectChange = (selectedOption, field) => {
    console.log(`Selected Option: ${JSON.stringify(selectedOption)}, Field: ${field}`);

    setPackageData((prev) => {
      const updatedData = { ...prev, [field]: selectedOption?.value || '' };

      if (field === 'account_id') {
        updatedData.consignee = selectedOption?.label || '';
      }

      if (field === 'delivery_service_id') {
        updatedData.deliveryService = {
          id: selectedOption?.value || '',
          name: selectedOption?.label || '',
        };

        if (selectedOption?.value === 'DROPOFF') {
          updatedData.tracking_number = generateDropoffTrackingNumber();
        }
      }

      console.log("Updated Package Data:", updatedData); // Check if updates are reflected
      return updatedData;
    });
  };



  return (
    <Layout>
      {/* Delete Confirmation Modal */}
      {deleteId ? (
        <dialog id="delete_modal" className="modal modal-open">
          <div className="modal-box">
            <h3 className="text-lg font-bold">Delete {packageData.airWayBill}</h3>
            <p className="py-4">Are you sure you want to delete {packageData.airWayBill}?</p>
            <div className='flex space-x-4 justify-end'>
              <button className='btn bg-red-400 text-white' onClick={() => handleDelete(packageData.id)}>Delete</button>
              <button className='btn' onClick={() => setDeleteId(null)}>Close</button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop"></form>
        </dialog>
      ) : null}

      {/* {printMode ? (
    <dialog id="delete_modal" className="modal modal-open">
    <div className="modal-box">
      <h3 className="text-lg font-bold">Print {packageData.tracking_number}</h3>
      <p className="py-4">Are you sure you want to Print {packageData.tracking_number}?</p>
      <div className='flex space-x-4 justify-end'>
        <button className='btn bg-green-500 text-white' onClick={() => handleDownload(packageData.tracking_number)}>Print</button>
        <button className='btn' onClick={() => setPrintMode(false)}>Close</button>
      </div>
    </div>
    <form method="dialog" className="modal-backdrop"></form>
  </dialog>
      ) : null} */}

      {loading && <Spinner />}

      <div className="flex flex-col flex-1 h-full max-h-full min-w-0 overflow-hidden bg-base-100 shadow-xl rounded-xl p-4 sm:p-6">
        <section className="flex flex-col sm:flex-row justify-between items-center mb-4 flex-shrink-0">
          <h2 className="text-2xl sm:text-3xl font-semibold">Packages</h2>

          {user && user.role == "Courier" ? null : (

            <div>
              <button className="btn bg-blue-800 text-white mt-2 sm:mt-0 mr-2" onClick={() => setShowBulkModal(true)}>
                Bulk Upload
              </button>
              <button className="btn bg-blue-800 text-white mt-2 sm:mt-0" onClick={() => handleOpenModal()}>
                Add Package
              </button>
            </div>
          )}
        </section>

        {error && <div className="alert alert-error flex-shrink-0">{error}</div>}

        <section className="mb-4 flex-shrink-0">
          {/* <FilterSection onFilterChange={handleFilterChange} onFilterReset={fetchPackages} statuses={status} /> */}
        </section>

        <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
          <DataTable
            data={packages}
            columns={packageColumns}
            itemsPerPage={50}
            searchable
            onSearch={handleSearch}
            totalItems={metaData.totalItems}
            totalPages={metaData.totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            className="bg-white rounded-lg shadow"
          />
        </div>
      </div>

      {/* Show Bulk Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-10 flex items-center justify-center p-4">

          <div className="bg-white w-full max-w-[640px] rounded-xl shadow-2xl overflow-hidden flex flex-col">

            <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-100 ">
              <h1 className="text-[#141414] font-bold leading-tight">Bulk Import Packages</h1>
              <button className="text-neutral-400 hover:text-primary transition-colors" onClick={() => {
                setShowBulkModal(false);
                setBulkFile(null);
              }}>
                X
              </button>
            </div>

            <div className="p-6 overflow-y-auto">

              <div className="flex flex-col mb-8">
                <label
                  htmlFor="bulk-file-input"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-blue-500');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-blue-500');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500');
                    const file = e.dataTransfer.files[0];
                    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx'))) {
                      setBulkFile(file);
                    } else {
                      toast.error('Please upload a .csv or .xlsx file');
                    }
                  }}
                  className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-neutral-200 bg-neutral-50 px-6 py-12 hover:border-primary/50 transition-colors cursor-pointer group"
                >
                  <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                    <FaCloudUploadAlt className='text-5xl text-[#002e63]' />
                  </div>
                  <div className="flex flex-col items-center gap-1 text-center">
                    {bulkFile ? (
                      <>
                        <p className="text-[#141414] text-base font-bold leading-tight tracking-tight">
                          {bulkFile.name}
                        </p>
                        <p className="text-neutral-500 text-xs font-normal">
                          {(bulkFile.size / 1024).toFixed(2)} KB
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[#141414] text-base font-bold leading-tight tracking-tight">
                          Drag and drop your file here, or click to browse
                        </p>
                        <p className="text-neutral-500 text-xs font-normal">
                          Supports .csv, .xlsx up to 10MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="bulk-file-input"
                    type="file"
                    accept=".csv,.xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setBulkFile(file);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="mt-2 flex min-w-[120px] items-center justify-center overflow-hidden rounded-lg h-10 px-6 bg-white border border-neutral-200 text-[#141414] text-sm font-bold shadow-sm hover:bg-neutral-50 transition-colors"
                  >
                    <span className="truncate">{bulkFile ? 'Change File' : 'Browse Files'}</span>
                  </button>
                </label>
              </div>

            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/50">
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkFile(null);
                }}
                className="flex min-w-[100px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 px-6 bg-transparent text-neutral-600 text-sm font-bold hover:bg-neutral-100 transition-colors"
              >
                <span className="truncate">Cancel</span>
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={!bulkFile || loading}
                className="flex bg-[#002e63] min-w-[160px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 px-8 text-white text-sm font-bold shadow-lg hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="truncate">{loading ? 'Uploading...' : 'Upload Packages'}</span>
              </button>
            </div>
          </div>
        </div>
      )}


      {modalOpen && (
        <dialog ref={dialogRef} className="modal modal-open bg-gray-50 overflow-y-auto">
          <form
            className="modal-box p-6 max-h-[90vh] max-w-max"
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              const isTextArea = e.target.tagName === "TEXTAREA";
              const isSelect = e.target.classList.contains("react-select__input");
              const isButton = e.target.tagName === "BUTTON";

              // Prevent form submission when pressing Enter in fields other than buttons
              if (e.key === "Enter" && !isTextArea && !isSelect && !isButton) {
                e.preventDefault();
              }

              // Allow form submission when pressing Enter on a button
              if (e.key === "Enter" && isButton) {
                handleSubmit(e);
              }
            }}
          >
            <h3 className="font-bold text-xl mb-4">
              {editMode ? "Edit Package" : "Add Package"}
            </h3>

            <div className="grid grid-cols-3 gap-6">
              {/* Tracking Number */}
              <div className="form-control col-span-2">
                <label className="label">
                  <span className="label-text">Tracking Number</span>
                </label>
                <input
                  type="text"
                  name="tracking_number"
                  value={packageData.tracking_number || ""}
                  onChange={handleInputChange}
                  className="input input-bordered"
                  required
                  ref={firstInputRef} // Auto-focus this field when modal opens
                />
              </div>

              {/* Delivery Service */}
              <div className="form-control col-span-1">
                <label className="label">
                  <span className="label-text">Delivery Service</span>
                </label>
                <CreatableSelect
                  {...selectConfig(deliveryServiceOptions, "delivery_service_id", "Select Delivery Service", false)}
                  value={deliveryServiceOptions.find(option => option.value === packageData.delivery_service_id) || null}
                />
              </div>

              {/* Shipper */}
              <div className="form-control col-span-1">
                <label className="label">
                  <span className="label-text">Shipper/Merchant</span>
                </label>
                <CreatableSelect
                  {...selectConfig(couriersOptions, "shipper_id", "Select Delivery Service")}
                  value={couriersOptions.find(option => option.value === packageData.shipper_id) || null}
                />
              </div>

              {/* Account */}
              <div className="form-control col-span-2">
                <label className="label">
                  <span className="label-text">Account</span>
                </label>
                <CreatableSelect
                  {...selectConfig(options, "account_id", "Select Account", false)}
                  value={options.find(option => option.value === packageData.account_id) || null}
                  formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                />
              </div>

              {/* Shipment */}
              <div className="form-control col-span-2">
                <label className="label">
                  <span className="label-text">Shipment</span>
                </label>
                <CreatableSelect
                  {...selectConfig(shipmentsOptions, "shipment_id", "Select Shipment", false)}
                  value={shipmentsOptions.find(option => option.value === packageData.shipment_id) || null}
                />
              </div>

              <FormField
                label="Weight (Lbs)"
                name="weight"
                type="number"
                value={packageData.weight}
                onChange={handleInputChange}
              />

              <div className="form-control col-span-2">
                <label className="label">
                  <span className="label-text">Package Type</span>
                </label>
                <CreatableSelect
                  {...selectConfig(packageTypeOptions, "package_type_id", "Select Package Type", false)}
                  value={packageTypeOptions.find(option => option.value === packageData.package_type_id) || null}
                />
              </div>

              <div className="form-control col-span-2">
                <label className="label">
                  <span className="label-text">Shipment Type</span>
                </label>
                <CreatableSelect
                  {...selectConfig(shipmentMethodOptions, "shipment_type_id", "Select Shipment Type", false)}
                  value={shipmentMethodOptions.find(option => option.value === packageData.shipment_type_id) || null}
                />
              </div>

              <FormField
                label="Package Description"
                name="description"
                type="text"
                value={packageData.description}
                onChange={handleInputChange}
                colSpan={2}
              />

              <FormField
                label="Package Value (USD)"
                name="package_value"
                type="number"
                value={packageData.package_value}
                onChange={handleInputChange}
              />

              {/* Notes Field */}
              <FormField
                label="Notes"
                name="notes"
                type="text"
                value={packageData.notes}
                onChange={handleInputChange}
                colSpan={2}
              />

              {/* Consignee Field */}
              <FormField
                label="Consignee"
                name="consignee"
                type="text"
                value={packageData.consignee}
                onChange={handleInputChange}
              />
            </div>

            {/* Package History Section */}
            <PackageHistoryTable histories={packageData.statusHistories} />

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-6">
              <button type="button" className="btn" onClick={handleModalClose}>
                Cancel
              </button>
              {editMode ? (
                <SubmitButton icon={<FaSave />} label="Update" />
              ) : (
                <>
                  <SubmitButton
                    icon={<FaSave />}
                    label="Save and Print"
                    onClick={() => setPrintMode(true)}
                  />
                  <SubmitButton
                    icon={<FaSave />}
                    label="Save and View"
                    onClick={() => setViewMode(true)}
                  />
                </>
              )}
            </div>
          </form>
        </dialog>
      )}


      <PackageDetailsModal
        viewMode={viewMode}
        setViewMode={setViewMode}
        packageData={packageData}
        setPackageData={setPackageData}
        handleEdit={handleEdit}
        setDeleteId={setDeleteId}
        handleDownload={handleDownload}
        handleModalClose={handleModalClose}
        saveNotes={saveNotes}
      />



      {modalDeleteOpen && (
        <dialog id="delete_modal" className="modal modal-open">
          <div className="modal-box">
            <h3 className="text-lg font-bold">Delete {packageData.tracking_number}</h3>
            <p className="py-4">Are you sure you want to delete {packageData.tracking_number}?</p>
            <div className='flex space-x-4'>
              <button className='btn bg-red-400 text-white' onClick={() => handleDelete(deleteId)}>Delete</button>
              <button className='btn' onClick={() => setModalDeleteOpen(false)}>Close</button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop"></form>
        </dialog>)}

    </Layout>
  );
};



export default Packages;

// Reusable Form Field Component
const FormField = ({ label, name, type, value, onChange, colSpan = 1 }) => (
  <div className={`form-control col-span-${colSpan}`}>
    <label className="label">
      <span className="label-text">{label}</span>
    </label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      className="input input-bordered"
    />
  </div>
);

// Package History Table Component
const PackageHistoryTable = ({ histories }) => (
  <div className="mt-8">
    <h4 className="font-bold text-lg mb-4">Package History</h4>
    <div className="bg-gray-100 p-4 rounded">
      {histories?.length > 0 ? (
        <table className="table-auto w-full border-collapse border border-gray-300">
          {/* Table headers and rows */}
        </table>
      ) : (
        <p>No history available.</p>
      )}
    </div>
  </div>
);

// Reusable Submit Button Component
const SubmitButton = ({ icon, label, ...props }) => (
  <button type="submit" className="btn btn-primary" {...props}>
    {icon}
    {label}
  </button>)